import { $Call } from 'utility-types';
import React from 'react';
import { useRouter } from 'next/router';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { classnames } from 'Utils/classnames/classnames.utils';
import { LabeledIconsList } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { AccommodationRoomArrangement } from './arrangement/AccommodationRoomArrangement';
import { AccommodationRoomAvailability } from './availability/AccommodationRoomAvailability';
import { AccommodationRoomDates } from './dates/AccommodationRoomDates';
import { AccommodationRoomDistribution } from './distribution/AccommodationRoomDistribution';
import { AccommodationRoomInfos } from './infos/AccommodationRoomInfos';
import { AccommodationRoomSharedRoom } from './shared-room/AccommodationRoomSharedRoom';
import { AccommodationRoomSlideshow } from './slideshow/AccommodationRoomSlideshow';
import { AccommodationRoomTag } from './tag/AccommodationRoomTag';
import { AccommodationRoomTitle } from './title/AccommodationRoomTitle';
import { themes } from './AccommodationRoom.themes';

const Detail = ({
  isPremium,
  items,
  theme,
  title,
}: {
  isPremium?: boolean;
  items: Array<AccommodationEquipment> | null | undefined;
  theme: keyof typeof themes;
  title: string;
}) => {
  const { thDetail, thDetailTitle, thDetailListTheme } = themes()[theme];
  return items?.length ? (
    <div
      className={classnames(thDetail, {
        'order-first': isPremium,
      })}
    >
      <Title className={thDetailTitle} hLevel={3} theme="title-sans-n3">
        {title}
      </Title>
      <LabeledIconsList
        items={items.map(({ id, label }) => ({
          icon: ICONS[id] || ICONS.TRIDENT1,
          labels: [label],
        }))}
        theme={thDetailListTheme}
      />
    </div>
  ) : null;
};

export type Props = AccommodationRoomEntity & {
  accommodationsComparator?: Node;
  className?: string;
  hasDates?: boolean;
  hasHiddenStock?: boolean;
  isBooking?: boolean;
  isInMoreRoomLayer?: boolean;
  isMobile: boolean;
  proposal?: Proposal | null | undefined;
  theme?: keyof $Call<typeof themes>;
};
export const AccommodationRoom = ({
  accommodationsComparator,
  bathroomEquipments = [],
  categoryId,
  className,
  dates,
  description,
  distribution,
  equipments = [],
  floorPlans = [],
  hasArrangement = false,
  hasDates = false,
  hasDistribution = false,
  hasSharedRoom = false,
  hasHiddenStock = false,
  id,
  images,
  infosList,
  isBestFamilyDeal = false,
  isBooking = false,
  isCabin = false,
  isExclusiveCollectionRoom = false,
  isExclusiveCollectionSpace = false,
  isInMoreRoomLayer = false,
  isMobile = false,
  isPremium = false,
  name,
  outsideFacilities = [],
  proposal,
  quantity,
  services = [],
  servicesIconsList,
  stock,
  streetView,
  theme = 'default',
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const {
    thArrangement,
    thAvailability,
    thContent,
    thDates,
    thDesc,
    thDistribution,
    thHeader,
    thHeaderElements,
    thInfos,
    thRoot,
    thSeemore,
    thSeemoreBtn,
    thSeemoreContent,
    thShared,
    thSlideshowBlock,
    thSlideshow,
    thTag,
    thTitle,
  } = themes(isExclusiveCollectionSpace, isExclusiveCollectionRoom, isMobile)[theme];
  const { query: queryParams } = useRouter();
  const isAccommodationView = queryParams.bookingStep === 'accommodation';
  const isAdditionalAccommodationsLayer = queryParams.openLayer === 'AdditionalAccommodationsLayer';
  return (
    <div className={classnames(thRoot, className)} id={id}>
      <div className={thHeader}>
        {hasDates && <AccommodationRoomDates className="mb-10" dates={dates} />}
        <AccommodationRoomTitle
          className={thTitle}
          isExclusiveCollectionSpace={isExclusiveCollectionSpace}
          name={name}
          quantity={quantity}
        />
        {theme !== 'default' && <div className="my-10">{accommodationsComparator}</div>}
        {!hasHiddenStock && (
          <AccommodationRoomAvailability
            className={thAvailability}
            isCabin={isCabin}
            stock={stock}
            theme={theme}
          />
        )}
      </div>
      {hasDates && (
        <p className={thDates}>
          <AccommodationRoomDates dates={dates} />
        </p>
      )}
      <div className={thSlideshowBlock}>
        <AccommodationRoomSlideshow
          accommodationName={name}
          className={thSlideshow}
          images={images}
          isCabin={isCabin}
          floorPlans={floorPlans}
          isExclusiveCollection={isExclusiveCollectionRoom || isExclusiveCollectionSpace}
          streetView={streetView}
        />
        {isBestFamilyDeal && (
          <AccommodationRoomTag
            className={thTag}
            label={t('booking.accommodation.bestFamilyDealLabel')}
          />
        )}
        {(isExclusiveCollectionSpace || isExclusiveCollectionRoom) && (
          <AccommodationRoomInfos
            className={thInfos}
            isFilterableInfoList
            isInMoreRoomLayer={isInMoreRoomLayer}
            items={infosList}
            proposal={proposal}
            roomCategoryId={categoryId}
            theme={theme}
            isAccommodationSummary={false}
          />
        )}
      </div>
      <div className={thContent}>
        {!isExclusiveCollectionSpace && !isExclusiveCollectionRoom && (
          <AccommodationRoomInfos
            className={thInfos}
            isFilterableInfoList
            isInMoreRoomLayer={isInMoreRoomLayer}
            items={infosList}
            proposal={proposal}
            roomCategoryId={categoryId}
            theme={theme}
            isAccommodationSummary={false}
          />
        )}
        {servicesIconsList && (
          <AccommodationRoomInfos
            className={thInfos}
            isExclusiveCollection
            isInMoreRoomLayer={isInMoreRoomLayer}
            isScrollable={false}
            items={servicesIconsList}
            proposal={proposal}
            theme={theme}
            isAccommodationSummary={false}
          />
        )}
        <div>
          <div data-testid="accommodationRoomHeader" className={thHeaderElements}>
            {((isAccommodationView && !isAdditionalAccommodationsLayer) || isBooking) && (
              <div className="w-full">
                <div className="flex flex-wrap">
                  <AccommodationRoomTitle
                    className={thTitle}
                    isExclusiveCollectionSpace={isExclusiveCollectionSpace}
                    name={name}
                    quantity={quantity}
                  />
                  {accommodationsComparator}
                </div>
                {!hasHiddenStock && (
                  <AccommodationRoomAvailability
                    className={thAvailability}
                    isCabin={isCabin}
                    stock={stock}
                    theme={theme}
                  />
                )}
              </div>
            )}
          </div>
          <Text
            className={classnames(
              thDesc,
              isExclusiveCollectionSpace ? 'text-ultramarine' : 'text-black',
            )}
            text={description}
          />
          {hasDistribution && (
            <AccommodationRoomDistribution
              className={thDistribution}
              distribution={distribution}
              isCabin={isCabin}
            />
          )}
          {hasArrangement && (
            <AccommodationRoomArrangement className={thArrangement} isCabin={isCabin} />
          )}
          {hasSharedRoom && <AccommodationRoomSharedRoom className={thShared} />}
        </div>
      </div>
      <SeeMore
        className={classnames(thSeemore, 'mt-15')}
        classNameButton={thSeemoreBtn}
        isCollapseEnabled
        onClick={() =>
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
            detail_click: 'see_details',
          })
        }
        seeMoreLabel={t('booking.accommodation.packageContent.showFullDescription')}
        seeLessLabel={t('booking.accommodation.packageContent.lessDetails')}
        seeMoreSize={80}
      >
        <div className={thSeemoreContent}>
          <Detail
            items={equipments}
            theme={theme}
            title={t('booking.accommodation.packageContent.equipment')}
          />
          <Detail
            items={bathroomEquipments}
            theme={theme}
            title={t('booking.accommodation.packageContent.bathroomEquipments')}
          />
          <Detail
            isPremium={isPremium}
            items={services}
            theme={theme}
            title={t('booking.accommodation.packageContent.services')}
          />
          <Detail
            items={outsideFacilities}
            theme={theme}
            title={t('booking.accommodation.packageContent.outsideEquipments')}
          />
        </div>
      </SeeMore>
    </div>
  );
};
